import { FormEvent, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import brandIcon from "./assets/brand/icon-no-bg.png";
import { localeOptions, type SupportedLocale } from "./i18n/generated";
import { STORE_LINKS } from "./storeBadges";

// Flipped to true (with the real Ko-fi URL) in rollout step 4, after the
// IAP-free app update passes store review. Web-only change, no re-review.
const SHOW_DONATE = false;
const KO_FI_URL = "";

const FEEDBACK_ENDPOINT = "/feedback/api.php";
const MESSAGE_MAX_LENGTH = 2000;
const EMAIL_MAX_LENGTH = 254;
const OS_VERSION_MAX_LENGTH = 80;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const APP_STORE_REVIEW_URL = `${STORE_LINKS.appStore}?action=write-review`;

type ThemeName = "light" | "dark";
type ThemePreference = ThemeName | "system";
type SubmitState = "idle" | "sending" | "success" | "error";
const THEME_OPTIONS: ThemePreference[] = ["system", "light", "dark"];

interface SupportPageProps {
  handleLanguageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  handleThemeChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  locale: SupportedLocale;
  localeOption: (typeof localeOptions)[number];
  theme: ThemeName;
  themeLabels: Record<ThemePreference, string>;
  themePreference: ThemePreference;
}

export default function SupportPage({
  handleLanguageChange,
  handleThemeChange,
  locale,
  localeOption,
  theme,
  themeLabels,
  themePreference,
}: SupportPageProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedMessage = message.trim();
    const trimmedEmail = email.trim();

    if (!trimmedMessage) {
      setValidationError(t("support.messageRequired"));
      return;
    }
    if (
      trimmedEmail &&
      (trimmedEmail.length > EMAIL_MAX_LENGTH ||
        !EMAIL_PATTERN.test(trimmedEmail))
    ) {
      setValidationError(t("support.emailInvalid"));
      return;
    }

    setValidationError(null);
    setSubmitState("sending");

    try {
      const response = await fetch(FEEDBACK_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          schema: "vritti.feedback",
          version: 1,
          message: trimmedMessage.slice(0, MESSAGE_MAX_LENGTH),
          ...(trimmedEmail ? { replyEmail: trimmedEmail } : {}),
          context: {
            appVersion: "web",
            platform: "web",
            osVersion: (window.navigator.userAgent || "unknown").slice(
              0,
              OS_VERSION_MAX_LENGTH,
            ),
            language: locale,
            theme,
          },
          createdAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setSubmitState("success");
      setMessage("");
      setEmail("");
    } catch {
      setSubmitState("error");
    }
  };

  const isSending = submitState === "sending";

  return (
    <div className="sup" data-theme={theme} dir={localeOption.dir}>
      <header className="sup-bar">
        <a className="sup-brand" href="/">
          <img className="sup-brand__icon" src={brandIcon} alt="" />
          <span>{t("meta.appName")}</span>
        </a>

        <div className="sup-bar__controls">
          <label className="sup-select">
            <span className="sup-select__label">
              {t("landing.languageLabel")}
            </span>
            <select value={locale} onChange={handleLanguageChange}>
              {localeOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="sup-select">
            <span className="sup-select__label">{t("landing.themeLabel")}</span>
            <select value={themePreference} onChange={handleThemeChange}>
              {THEME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {themeLabels[option]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <main className="sup-main">
        <div className="sup-open">
          {/* Signature: 4s inhale / 6s exhale — the app's own rhythm. */}
          <div className="sup-pacer" aria-hidden="true">
            <span className="sup-pacer__dot" />
          </div>
          <h1 className="sup-title">{t("support.title")}</h1>
          <p className="sup-intro">{t("support.intro")}</p>
        </div>

        <section className="sup-block sup-block--primary">
          <p className="sup-eyebrow">{t("support.feedbackTitle")}</p>
          <p className="sup-lede">{t("support.feedbackIntro")}</p>

          <form className="sup-form" onSubmit={handleSubmit} noValidate>
            <label className="sup-field">
              <span className="sup-field__label">
                {t("support.messageLabel")}
              </span>
              <textarea
                value={message}
                maxLength={MESSAGE_MAX_LENGTH}
                rows={5}
                placeholder={t("support.messagePlaceholder")}
                onChange={(event) => setMessage(event.target.value)}
              />
            </label>

            <label className="sup-field">
              <span className="sup-field__label">
                {t("support.emailLabel")}
              </span>
              <input
                type="email"
                value={email}
                maxLength={EMAIL_MAX_LENGTH}
                placeholder={t("support.emailPlaceholder")}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            {validationError ? (
              <p role="alert" className="sup-note sup-note--bad">
                {validationError}
              </p>
            ) : null}
            {submitState === "error" ? (
              <p role="alert" className="sup-note sup-note--bad">
                {t("support.error")}
              </p>
            ) : null}
            {submitState === "success" ? (
              <p role="status" className="sup-note sup-note--good">
                {t("support.success")}
              </p>
            ) : null}

            <button className="sup-btn" type="submit" disabled={isSending}>
              {isSending ? t("support.sending") : t("support.send")}
            </button>
          </form>
        </section>

        <section className="sup-block">
          <p className="sup-eyebrow">{t("support.rateTitle")}</p>
          <p className="sup-lede">{t("support.rateIntro")}</p>
          <div className="sup-row">
            <a
              className="sup-btn sup-btn--quiet"
              href={APP_STORE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
            >
              {t("support.rateAppStore")}
            </a>
            <a
              className="sup-btn sup-btn--quiet"
              href={STORE_LINKS.googlePlay}
              target="_blank"
              rel="noreferrer"
            >
              {t("support.rateGooglePlay")}
            </a>
          </div>
        </section>

        {SHOW_DONATE && KO_FI_URL ? (
          <section className="sup-block sup-block--aside">
            <p className="sup-eyebrow">{t("support.donateTitle")}</p>
            <p className="sup-lede">{t("support.donateIntro")}</p>
            <a
              className="sup-btn sup-btn--quiet"
              href={KO_FI_URL}
              target="_blank"
              rel="noreferrer"
            >
              {t("support.donateButton")}
            </a>
          </section>
        ) : null}

        <p className="sup-back">
          <a href="/">{t("support.backHome")}</a>
        </p>
      </main>
    </div>
  );
}
